import { perkpointApi } from "./client";

/** Test data generator (api/src/services/testData.js). Dev tool; runs in the background. */

export type TestDataOptions = {
  members: number;
  transactions_per_member: number;
  /** Share of transactions that are redemptions, 0–60. */
  redeem_pct: number;
  min_amount: number;
  max_amount: number;
  /** Spread enrolments and purchases over the last N days (capped by the program). */
  history_days: number;
  outlets: boolean;
  rules: boolean;
  fraud: boolean;
  campaigns: boolean;
  /** Fill campaign summary metrics (members reached, bonus earns, etc.). Defaults true if omitted. */
  campaign_activity: boolean;
  segments: boolean;
  member_mix: boolean;
  refresh_insights: boolean;
};

export type TestDataRun = {
  id: string;
  program_id: string;
  status: "running" | "done" | "cancelled" | "failed";
  phase: "setup" | "members" | "transactions" | "finishing" | "done";
  options: TestDataOptions;
  total: number;
  done: number;
  setup_created: number;
  members_created: number;
  transactions_posted: number;
  issuance_posted?: number;
  redemptions_posted?: number;
  failed: number;
  log: string[];
  error: string | null;
  started_at: string;
  finished_at: string | null;
  cancel_requested: boolean;
};

export type TestDataStatus = {
  enabled: boolean;
  max_history_days: number;
  running: boolean;
  latest: TestDataRun | null;
};

export const testDataKeys = {
  status: (programId: string) => ["perkpoint", "test-data", programId] as const,
  run: (programId: string, runId: string) => ["perkpoint", "test-data", programId, runId] as const,
};

const base = (programId: string) => `/admin/programs/${programId}/test-data`;

export const TestDataApi = {
  async status(programId: string) {
    const { data } = await perkpointApi.get<TestDataStatus>(base(programId));
    return data;
  },
  async start(programId: string, options: TestDataOptions) {
    const { data } = await perkpointApi.post<TestDataRun>(base(programId), options);
    return data;
  },
  async run(programId: string, runId: string) {
    const { data } = await perkpointApi.get<TestDataRun>(`${base(programId)}/${runId}`);
    return data;
  },
  async cancel(programId: string, runId: string) {
    const { data } = await perkpointApi.post<TestDataRun>(`${base(programId)}/${runId}/cancel`);
    return data;
  },
};
